/*
 * Toasts: short confirmations and failures that do not need a decision.
 *   import { toast } from '@/components/ui'
 *   toast.success(t('servers:saved'))
 *   toast.error(error)                      // an ApiError: translated problem title + correlation id
 *   toast.info(text, { description, action: { label, onClick } })
 * Failed mutations toast automatically unless their `meta.silent` is true (src/app/queryClient.ts).
 * <Toaster /> is mounted once by the app providers.
 */
import { CheckCircle, Info, Warning, WarningOctagon, X } from '@phosphor-icons/react'
import { Toast as T } from 'radix-ui'
import { useSyncExternalStore, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { isApiError } from '@/api/client'
import { cn } from '@/lib/cn'
import { problemMessage } from '@/lib/errors'

type Tone = 'success' | 'error' | 'info' | 'warning'
type ToastItem = {
  id: number
  tone: Tone
  title?: ReactNode
  description?: ReactNode
  error?: unknown
  action?: { label: string; onClick: () => void }
  duration?: number
}

let items: ToastItem[] = []
let seq = 0
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function push(tone: Tone, title: ReactNode | undefined, opts: Omit<ToastItem, 'id' | 'tone' | 'title'> = {}) {
  const id = ++seq
  items = [...items.slice(-3), { id, tone, title, ...opts }]
  emit()
  return id
}
function dismiss(id: number) {
  items = items.filter((i) => i.id !== id)
  emit()
}

type Opts = { description?: ReactNode; action?: { label: string; onClick: () => void }; duration?: number }
export const toast = {
  success: (title: ReactNode, opts?: Opts) => push('success', title, opts),
  info: (title: ReactNode, opts?: Opts) => push('info', title, opts),
  warning: (title: ReactNode, opts?: Opts) => push('warning', title, opts),
  /** a message, or an error (ApiError shows the translated problem and its reference) */
  error: (messageOrError: string | unknown, opts?: Opts) =>
    typeof messageOrError === 'string'
      ? push('error', messageOrError, opts)
      : push('error', undefined, { ...opts, error: messageOrError }),
  dismiss,
}

const ICON: Record<Tone, { Icon: typeof Info; cls: string }> = {
  success: { Icon: CheckCircle, cls: 'text-teal-600' },
  error: { Icon: WarningOctagon, cls: 'text-danger-600' },
  info: { Icon: Info, cls: 'text-brand-600' },
  warning: { Icon: Warning, cls: 'text-warm-700' },
}

function useToasts() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => items,
  )
}

export function Toaster() {
  const { t } = useTranslation(['common', 'errors'])
  const list = useToasts()
  return (
    <T.Provider swipeDirection="down" duration={5000} label={t('ui.notifications')}>
      {list.map((item) => {
        const { Icon, cls } = ICON[item.tone]
        const msg = item.error !== undefined ? problemMessage(item.error) : null
        const cid = isApiError(item.error) ? item.error.correlationId : null
        return (
          <T.Root
            key={item.id}
            duration={item.duration ?? (item.tone === 'error' ? 8000 : 5000)}
            onOpenChange={(o) => !o && dismiss(item.id)}
            className={cn(
              'pointer-events-auto flex w-full items-start gap-3 rounded-2xl bg-surface p-4 shadow-float ring-1 ring-card-line',
              'data-[state=closed]:animate-fade-out data-[state=open]:animate-toast-in data-[swipe=end]:animate-fade-out data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)]',
            )}
          >
            <Icon className={cn('mt-0.5 size-5 shrink-0', cls)} weight="fill" aria-hidden="true" />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <T.Title className="text-14 font-semibold text-ink">{item.title ?? msg?.title}</T.Title>
              {item.description || msg ? (
                <T.Description className="text-13 leading-5 text-muted">
                  {item.description ?? msg?.detail}
                </T.Description>
              ) : null}
              {cid ? (
                <span className="text-12 text-subtle">
                  {t('errors:reference')}{' '}
                  <bdi dir="ltr" className="font-mono">
                    {cid.slice(0, 18)}
                  </bdi>
                </span>
              ) : null}
              {item.action ? (
                <T.Action altText={item.action.label} asChild>
                  <button
                    type="button"
                    onClick={item.action.onClick}
                    className="mt-1 w-fit text-13 font-semibold text-brand-700 hover:underline"
                  >
                    {item.action.label}
                  </button>
                </T.Action>
              ) : null}
            </div>
            <T.Close
              aria-label={t('actions.close')}
              className="-m-1 grid size-7 shrink-0 place-items-center rounded-lg text-subtle hover:bg-control hover:text-ink"
            >
              <X className="size-4" aria-hidden="true" />
            </T.Close>
          </T.Root>
        )
      })}
      <T.Viewport className="fixed end-0 bottom-0 z-[70] flex print:hidden w-full max-w-[24rem] flex-col gap-2 p-4 outline-none sm:p-6" />
    </T.Provider>
  )
}
